import type { WidgetProps } from "../registry";
import type { ProgramPatchPayload } from "../../../generated-api";
import { useVideoSelector } from "../../../hooks/useVideoSearch";
import { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  submitProgramPatchPipeline,
  VideoResultType,
  VideoSelectionMap,
} from "../actions/patchProgram";
import { client } from "../../../lib/api";

function ExerciseVideoBlock({
  query,
  index,
  onVideoSelect,
}: {
  query: string;
  index: number;
  onVideoSelect: (index: number, video: VideoResultType) => void;
}) {
  const { results, selectedVideo, selectedIndex, setSelectedIndex, isLoading } =
    useVideoSelector(query);

  // Track state changes by syncing with unique URL strings
  const lastSyncRef = useRef<string | null>(null);

  if (selectedVideo && lastSyncRef.current !== selectedVideo.url) {
    lastSyncRef.current = selectedVideo.url;
    setTimeout(() => {
      // Cast safely into your matching exact type layout
      onVideoSelect(index, selectedVideo as VideoResultType);
    }, 0);
  }

  return (
    <div className="mt-2">
      <div className="aspect-video w-full rounded-lg overflow-hidden border border-border-subtle bg-black">
        {selectedVideo?.embedUrl ? (
          <iframe
            src={selectedVideo.embedUrl}
            className="w-full h-full"
            allowFullScreen
          />
        ) : (
          <div className="text-xs text-text-tertiary flex items-center justify-center h-full">
            {isLoading ? "Loading videos..." : "No video available"}
          </div>
        )}
      </div>

      {results.length > 1 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {results.map((v, i) => (
            <button
              key={v.url}
              type="button"
              onClick={() => setSelectedIndex(i)}
              className={`text-xs px-2 py-1 rounded border ${
                i === selectedIndex
                  ? "bg-indigo-600 text-white"
                  : "border-border"
              }`}
            >
              {v.title?.slice(0, 25) ?? `Video ${i + 1}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProgramPatchWidget({
  payload,
  onComplete,
}: WidgetProps<ProgramPatchPayload>) {
  const queryClient = useQueryClient();
  const result = useQuery({
    queryKey: ["program", payload.id],
    queryFn: () => client.programs.getProgram({ id: payload.id }),
  });

  const program = result?.data?.program;
  const [videoSelections, setVideoSelections] = useState<VideoSelectionMap>({});

  const patchProgramMutation = useMutation({
    mutationFn: () => submitProgramPatchPipeline({ payload, videoSelections }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] });
      queryClient.invalidateQueries({ queryKey: ["videos"] });

      onComplete({
        accepted: true,
        message: `Updated "${payload.id}"`,
      });
    },
  });

  const handleVideoSelect = (index: number, video: VideoResultType) => {
    setVideoSelections((prev) => ({
      ...prev,
      [index]: { videoData: video },
    }));
  };

  return (
    <div className="flex flex-col gap-3 mt-1 border border-border-subtle rounded-xl p-3 text-sm">
      {/* Header */}
      <div>
        <div className="font-semibold text-text-primary text-base">
          {program?.title}
        </div>
        {program?.description && (
          <div className="text-text-secondary text-xs mt-1">
            {program?.description}
          </div>
        )}
      </div>

      {/* Exercises */}
      {payload?.addProposals?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-text-primary mb-1">
            Program exercises ({payload.addProposals.length})
          </div>

          <ul className="flex flex-col gap-2 max-h-96 overflow-auto pr-1">
            {payload.addProposals.map((exercise, i) => (
              <li
                key={`${exercise.title}-${i}`}
                className="text-xs text-text-secondary border border-border-subtle rounded-md p-2"
              >
                <div className="text-text-primary font-medium">
                  {i + 1}. {exercise.title}
                </div>
                {exercise.description && (
                  <div className="mt-0.5">{exercise.description}</div>
                )}

                <ExerciseVideoBlock
                  query={exercise.query || exercise.title}
                  index={i}
                  onVideoSelect={handleVideoSelect}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Errors */}
      {patchProgramMutation.isError && (
        <div className="text-xs text-red-600 bg-red-50 p-2 rounded-md">
          {(patchProgramMutation.error as any)?.message ||
            "Failed to patch program"}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          disabled={patchProgramMutation.isPending}
          onClick={() => patchProgramMutation.mutate()}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {patchProgramMutation.isPending
            ? "Creating..."
            : "patch this program"}
        </button>

        <button
          type="button"
          disabled={patchProgramMutation.isPending}
          onClick={() => onComplete({ accepted: false })}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-border-subtle text-text-primary hover:bg-bg-secondary transition-colors"
        >
          Let's adjust it
        </button>
      </div>
    </div>
  );
}
